import React, { useEffect, useMemo, useState } from "react";
import { MapPin, ShieldCheck } from "lucide-react";
import { useAbarytDate } from "../context/AbarytDateContext";
import { getNextFeast } from "../lib/abarytCalendar";

export default function AbarytDateBar() {
  const {
    date,
    fallbackDate,
    status,
    requestLocation,
    continueWithoutLocation,
    needsLocationConsent,
    basis,
    basisTimeZone,
  } = useAbarytDate();

  const [clock, setClock] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const effectiveDate = date || fallbackDate;
  const nextFeast = getNextFeast(effectiveDate);
  const feastCountdown = nextFeast
    ? nextFeast.daysUntil === 0
      ? `Today · ${nextFeast.label}`
      : `${nextFeast.daysUntil} day${nextFeast.daysUntil === 1 ? "" : "s"} to ${nextFeast.label}`
    : "";

  const displayTime = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
        timeZone: basisTimeZone || undefined,
      }).format(clock);
    } catch {
      return new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
      }).format(clock);
    }
  }, [clock, basisTimeZone]);

  const basisLabel =
    basis === "precise"
      ? `Local sundown · ${basisTimeZone || "device time zone"}`
      : basis === "timezone"
        ? `Approx. sundown · ${basisTimeZone}`
        : "Africa/Türkiye reference sundown";

  const timeLabel =
    basis === "reference"
      ? "Reference time"
      : basis === "timezone"
        ? "Browser time"
        : "Local time";

  return (
    <>
      <div className="bg-royal-blue text-white border-b border-kingdom-gold/50" data-testid="abaryt-date">
        <div className="max-w-7xl mx-auto px-3 py-2 sm:px-4">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] sm:text-xs md:text-sm">
            <span className="font-semibold text-kingdom-gold">Abaryt</span>
            <span>
              {status === "loading"
                ? "Calculating local sundown…"
                : effectiveDate
                  ? `M${effectiveDate.month} · D${effectiveDate.day} · ${effectiveDate.year}`
                  : "Date unavailable"}
            </span>

            {effectiveDate && (
              <>
                <span className="text-white/35">|</span>
                <span className="hidden sm:inline">
                  <span className="font-semibold text-kingdom-gold">Gregorian</span>
                  <span className="ml-1.5">{effectiveDate.gregorianLabel}</span>
                </span>
                <span className="sm:hidden">{effectiveDate.gregorianLabel}</span>

                {feastCountdown && (
                  <>
                    <span className="text-white/35 hidden sm:inline">|</span>
                    <span className="rounded-full bg-kingdom-gold px-2.5 py-0.5 font-bold text-royal-blue">
                      {feastCountdown}
                    </span>
                  </>
                )}
              </>
            )}
          </div>

          {effectiveDate && (
            <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] sm:text-[11px] md:text-xs text-white/80">
              <span>
                <span className="text-white/60">{timeLabel}</span>
                <span className="ml-1.5">{displayTime}</span>
                {basisTimeZone && <span className="ml-1.5 text-white/65">{basisTimeZone}</span>}
              </span>
              <span className="text-white/30">|</span>
              <span>{basisLabel}</span>
              {basis !== "precise" && (
                <button
                  type="button"
                  onClick={requestLocation}
                  className="font-semibold text-kingdom-gold underline underline-offset-2"
                >
                  Use precise local sundown
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {needsLocationConsent && (
        <div
          className="fixed inset-0 z-[250] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:px-4 sm:py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sundown-location-title"
          data-testid="sundown-location-privacy-dialog"
        >
          <div className="w-full sm:max-w-lg max-h-[88dvh] overflow-y-auto bg-white border-t-4 border-kingdom-gold shadow-2xl rounded-t-2xl sm:rounded-none">
            <div className="p-5 sm:p-7">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-kingdom-gold/15 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-royal-blue" aria-hidden="true" />
                </div>
                <div>
                  <h2 id="sundown-location-title" className="font-heading text-xl sm:text-2xl text-royal-blue">
                    Use your precise local sundown?
                  </h2>
                  <p className="mt-2 text-sm sm:text-base text-slate-700 leading-relaxed">
                    The Abaryt day changes at sundown. If you share location, this page can calculate your
                    actual seasonal sunset. If you do not share it, we use your browser time zone to estimate
                    a seasonal sundown instead.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex gap-3 bg-slate-50 border border-slate-200 p-3.5">
                <ShieldCheck className="w-5 h-5 text-royal-blue shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  <strong>Privacy:</strong> coordinates stay only in this open page's memory for the sundown
                  calculation. They are not sent to our server, MongoDB, analytics, cookies, localStorage,
                  or sessionStorage.
                </p>
              </div>

              <p className="mt-3 text-[11px] sm:text-xs text-slate-500 leading-relaxed">
                This explanation appears no more than twice in a browser session. You can always use the
                precise-local-sundown link in the date bar later.
              </p>
            </div>

            <div className="sticky bottom-0 grid grid-cols-1 sm:grid-cols-2 gap-2 border-t border-slate-200 bg-white p-4">
              <button type="button" onClick={requestLocation} className="btn-gold min-h-11 px-5 py-2.5 font-semibold">
                Use my location
              </button>
              <button
                type="button"
                onClick={continueWithoutLocation}
                className="min-h-11 px-5 py-2.5 border border-royal-blue text-royal-blue font-semibold hover:bg-royal-blue/5"
              >
                Continue without sharing
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
